import { useState } from "react";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { reviews } from "@/mocks/reviews";

export default function AboutPage() {
  const [showContactForm, setShowContactForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const formUrl = "https://form.readdy.ai/api/submit/contact-form";

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }
    const formData = new FormData(form);
    fetch(formUrl, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(() => setFormSubmitted(true))
      .catch(() => setFormSubmitted(true));
  };

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "禁漫天堂",
    url: "https://jmtt.example.com",
    description: "禁漫天堂是广受用户关注的漫画阅读平台，提供海量漫画资源和优质阅读体验。",
    contactPoint: {
      "@type": "ContactPoint",
      email: "support@jmtt.example.com",
      contactType: "customer service",
      availableLanguage: "Chinese",
    },
  };

  return (
    <>
      <SeoHead
        title="关于我们 – 禁漫天堂 | 品牌介绍、用户口碑与联系方式"
        description="了解禁漫天堂的品牌故事与发展历程，查看真实用户评价与口碑，获取官方联系方式。禁漫天堂致力于为漫画爱好者提供最佳的阅读体验。"
        keywords="禁漫天堂关于我们,禁漫天堂品牌介绍,禁漫天堂联系方式,禁漫天堂用户评价"
        canonicalPath="/about"
        jsonLd={orgJsonLd}
      />

      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "关于我们" },
            ]}
          />

          <div className="mt-6 mb-10">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              关于我们
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
              了解禁漫天堂的品牌故事、发展历程与使命愿景，以及来自真实用户的反馈与评价。
            </p>
          </div>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                品牌介绍
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  禁漫天堂诞生于漫画爱好者对优质阅读体验的共同追求。自上线以来，平台始终坚持以用户为中心，致力于打造一个内容丰富、体验流畅、安全可靠的漫画阅读社区。
                </p>
                <p>
                  我们的团队由一群热爱漫画的工程师、设计师和内容运营组成。正是因为对漫画的共同热爱，我们深刻理解读者在寻找和阅读漫画过程中的每一个需求：从快速找到想看的作品、到舒适的阅读体验、再到与同好交流分享。
                </p>
                <p>
                  禁漫天堂平台涵盖了<strong>热血动作、恋爱言情、奇幻冒险、搞笑日常、悬疑推理、科幻未来</strong>等多种漫画类型，并建立了完善的分类体系和智能推荐算法，帮助每一位用户快速找到心仪的作品。
                </p>
                <p>
                  展望未来，禁漫天堂将继续优化产品体验，拓展内容资源，并积极探索新技术在漫画阅读领域的应用，为全球华语漫画读者提供更加卓越的服务。
                </p>
              </div>
            </div>

            <div className="relative w-full h-72 md:h-80 rounded-xl overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20artistic%20illustration%20of%20a%20cozy%20digital%20reading%20community%2C%20people%20enjoying%20comics%20on%20various%20devices%2C%20soft%20coral%20cream%20color%20palette%2C%20modern%20minimalist%20flat%20style%2C%20warm%20inviting%20atmosphere%2C%20no%20text&width=1000&height=700&seq=about-brand-01&orientation=landscape"
                alt="禁漫天堂品牌形象"
                className="w-full h-full object-cover"
              />
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: "ri-database-2-line", title: "海量资源", desc: "涵盖多种类型的丰富漫画内容库，持续更新上架" },
                { icon: "ri-speed-up-line", title: "极速加载", desc: "优化的图片加载技术，翻页流畅不卡顿" },
                { icon: "ri-device-line", title: "多端同步", desc: "PC、手机、平板无缝切换，阅读进度云端同步" },
                { icon: "ri-shield-check-line", title: "安全可靠", desc: "官方认证入口，数据加密传输，账号安全有保障" },
              ].map((item, i) => (
                <div key={i} className="bg-background-100 border border-background-200/70 rounded-xl p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
                  用户口碑
                </h2>
                <p className="text-foreground-500 text-sm">来自禁漫天堂真实用户的使用评价与反馈</p>
              </div>
              <div className="flex items-center gap-1.5 text-accent-500">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-half-fill"></i>
                <span className="text-foreground-700 text-sm font-medium ml-1">4.8</span>
                <span className="text-foreground-400 text-xs">/ 5.0 综合评分</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {reviews.slice(0, 8).map((review, index) => (
                <div key={index} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${review.avatar} text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-2 text-accent-500 text-xs">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill"></i>
                    ))}
                  </div>
                  <p className="text-foreground-600 text-xs leading-relaxed line-clamp-4">
                    {review.content}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                  如果您在使用禁漫天堂的过程中遇到任何问题，或有任何建议与反馈，欢迎通过以下方式与我们联系。我们的客服团队将尽快为您处理。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                      <p className="text-foreground-500 text-xs">support@jmtt.example.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                      <i className="ri-global-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">官方网站</p>
                      <p className="text-foreground-500 text-xs">jmtt.example.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {!showContactForm ? (
                  <div className="bg-background-100 border border-background-200/70 rounded-xl p-8 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-send-plane-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">给我们留言</h3>
                    <p className="text-foreground-500 text-sm mb-5">有任何问题或建议？请填写表单，我们会尽快回复。</p>
                    <button
                      onClick={() => setShowContactForm(true)}
                      className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-edit-line mr-1.5"></i>
                      填写留言表单
                    </button>
                  </div>
                ) : formSubmitted ? (
                  <div className="bg-background-100 border border-background-200/70 rounded-xl p-8 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-check-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">提交成功！</h3>
                    <p className="text-foreground-500 text-sm">感谢您的留言，我们会尽快回复您。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="bg-background-100 border border-background-200/70 rounded-xl p-6" data-readdy-form="">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-accent-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">电子邮箱</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-accent-400 transition-colors"
                          placeholder="请输入您的电子邮箱"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                      </div>
                      <div className="spam-shield">
                        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      </div>
                      <button
                        type="submit"
                        className="w-full px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-send-plane-line mr-1.5"></i>
                        提交留言
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}